import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Clock, Send, CheckCircle2, PoundSterling, Hammer, Info } from "lucide-react";
import { marketplaceApi } from "@/services/api";
import { fmtMoney, fmtDate, apiError } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const BID_TONE = {
  submitted: "bg-blue-100 text-blue-700",
  won: "bg-emerald-100 text-emerald-800",
  not_selected: "bg-slate-100 text-slate-600",
  withdrawn: "bg-slate-100 text-slate-500",
};
const BID_LABEL = { submitted: "Quote sent", won: "Won", not_selected: "Not selected", withdrawn: "Withdrawn" };

export default function ContractorLeads({ approved }) {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [bids, setBids] = useState([]);
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [av, mb] = await Promise.all([marketplaceApi.availableLeads(), marketplaceApi.myBids()]);
      setLeads(av.data?.leads || []);
      setBids(mb.data || []);
    } catch (e) { toast.error(apiError(e, "Could not load work")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setForm = (id, patch) => setForms((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const submitBid = async (lead) => {
    const form = forms[lead.id] || {};
    const amount = parseFloat(String(form.amount).replace(/[£,\s]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Enter your quote amount (ex VAT)."); return; }
    setBusy(lead.id);
    try {
      await marketplaceApi.submitBid(lead.id, { amount, message: form.message || "", proposed_start: form.start || null });
      toast.success(lead.my_bid ? "Quote updated" : "Quote sent");
      await load();
    } catch (e) { toast.error(apiError(e, "Could not send quote")); }
    finally { setBusy(""); }
  };

  const withdraw = async (lead) => {
    setBusy(lead.id);
    try { await marketplaceApi.withdrawBid(lead.id); toast.success("Quote withdrawn"); await load(); }
    catch (e) { toast.error(apiError(e, "Could not withdraw")); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-red-600" /></div>;

  if (!approved) {
    return (
      <Card className="p-8 text-center" data-testid="partner-leads-locked">
        <Clock className="mx-auto mb-3 h-8 w-8 text-amber-500" />
        <h3 className="font-semibold text-slate-800">Available work unlocks once you&apos;re approved</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Complete your profile under &ldquo;My profile&rdquo; and submit it for review. As soon as FD Check approves your account, remedial jobs in your postcode areas will appear here to quote for.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-leads">
      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-800">Available work</h2>
        {leads.length === 0 && (
          <Card className="p-8 text-center text-sm text-slate-500" data-testid="partner-leads-empty">
            No open jobs in your areas right now. Make sure your coverage postcodes are up to date under &ldquo;My profile&rdquo;.
          </Card>
        )}
        <div className="space-y-4">
          {leads.map((lead) => {
            const form = forms[lead.id] || {};
            const full = typeof lead.quotes_cap === "number" && lead.bids_count >= lead.quotes_cap && !lead.my_bid;
            return (
              <Card key={lead.id} className="p-5" data-testid={`partner-lead-${lead.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-800">{lead.title || "Remedial works"}</h3>
                    <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                      <MapPin className="h-3.5 w-3.5" /> {lead.building_name || "Building"} · {lead.building_postcode || "—"}
                    </p>
                  </div>
                  {lead.my_bid && <Badge className={`${BID_TONE[lead.my_bid.status] || ""} border-0`}>{BID_LABEL[lead.my_bid.status] || lead.my_bid.status}</Badge>}
                </div>

                {(lead.scope || []).length > 0 && (
                  <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3">
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Hammer className="h-3.5 w-3.5" /> Scope of works</p>
                    <ul className="space-y-0.5 text-sm text-slate-700">
                      {lead.scope.slice(0, 10).map((w, i) => (
                        <li key={i}>• {w.door} — {w.description}{w.quantity > 1 ? ` ×${w.quantity}` : ""}</li>
                      ))}
                      {lead.scope.length > 10 && <li className="text-slate-400">+{lead.scope.length - 10} more…</li>}
                    </ul>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  {typeof lead.estimated_value === "number" && lead.estimated_value > 0 && (
                    <span className="flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Indicative value {fmtMoney(lead.estimated_value)}</span>
                  )}
                  <span>{lead.bids_count} quote{lead.bids_count === 1 ? "" : "s"} so far{lead.quotes_cap ? ` (max ${lead.quotes_cap})` : ""}</span>
                </div>

                {full ? (
                  <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">This job already has the maximum number of quotes.</p>
                ) : (
                  <div className="mt-4 rounded-lg border border-slate-200 p-3">
                    <p className="mb-2 text-sm font-semibold text-slate-700">{lead.my_bid ? "Update your quote" : "Send your quote"}</p>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Your price ex VAT (£)</Label>
                        <Input type="number" inputMode="decimal" value={form.amount ?? (lead.my_bid ? lead.my_bid.amount : "")} onChange={(e) => setForm(lead.id, { amount: e.target.value })} data-testid={`partner-bid-amount-${lead.id}`} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Earliest start</Label>
                        <Input type="date" value={form.start ?? (lead.my_bid?.proposed_start || "").slice(0, 10)} onChange={(e) => setForm(lead.id, { start: e.target.value })} data-testid={`partner-bid-start-${lead.id}`} />
                      </div>
                      <div className="space-y-1.5 sm:col-span-1">
                        <Label className="text-xs text-slate-600">&nbsp;</Label>
                        <Button onClick={() => submitBid(lead)} disabled={busy === lead.id} className="w-full bg-red-600 hover:bg-red-700" data-testid={`partner-bid-submit-${lead.id}`}>
                          {busy === lead.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} {lead.my_bid ? "Update" : "Send quote"}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <Label className="text-xs text-slate-600">Message to the building owner (optional)</Label>
                      <Textarea rows={2} value={form.message ?? (lead.my_bid?.message || "")} onChange={(e) => setForm(lead.id, { message: e.target.value })} placeholder="Lead time, what's included, warranty…" data-testid={`partner-bid-message-${lead.id}`} />
                    </div>
                    {lead.my_bid && lead.my_bid.status === "submitted" && (
                      <button type="button" onClick={() => withdraw(lead)} className="mt-2 text-xs font-medium text-red-600 hover:underline" data-testid={`partner-bid-withdraw-${lead.id}`}>Withdraw my quote</button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-800">My quotes</h2>
        {bids.length === 0 ? (
          <Card className="p-6 text-center text-sm text-slate-500">You haven&apos;t sent any quotes yet.</Card>
        ) : (
          <div className="space-y-2" data-testid="partner-my-bids">
            {bids.map((b) => (
              <Card key={b.id} className="flex flex-wrap items-center justify-between gap-2 p-4">
                <div>
                  <p className="font-medium text-slate-800">{b.lead?.title || "Remedial works"} <span className="text-sm text-slate-400">· {b.lead?.building_postcode || "—"}</span></p>
                  <p className="text-sm text-slate-500">Your quote {fmtMoney(b.amount)}{b.updated_at ? ` · ${fmtDate(b.updated_at)}` : ""}</p>
                  {b.status === "won" && b.commission && (
                    <p className="mt-1 flex items-center gap-1 text-sm font-medium text-red-700"><PoundSterling className="h-3.5 w-3.5" /> Platform commission due: {fmtMoney(b.commission.fee)} ({b.commission.status})</p>
                  )}
                </div>
                <Badge className={`${BID_TONE[b.status] || ""} border-0`}>{BID_LABEL[b.status] || b.status}</Badge>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
